import type { FormModel } from './autoSaveModel';

/**
 * Phase 6e2a — shared types for the component-form chassis (Part 6 §49/§50)
 * + THE Q15 SHIM (§50.2): the frozen 14-member reach-in interface every
 * legacy selector touches on `current_model.view`. React forms register a
 * shim object per component id under `window.__FORM_SHIM__` (the 6d-spike
 * agreement) so the 6e2c selector cut consumes it without DOM reach-ins.
 */

// ---------------------------------------------------------------------------
// §50.1 type → urlRoot plural / partName / classID maps (per-type model layer
// ports as pure config — parity 343; no legacy model blobs, [NEG]#113).
// ---------------------------------------------------------------------------
export type ComponentTypeName =
  | 'fan'
  | 'coil'
  | 'filter'
  | 'eliminator'
  | 'evap_pack'
  | 'gas_heater'
  | 'silencer'
  | 'energy_wheel'
  | 'plate_heat_exchanger'
  | 'user_defined_component';

export const COMPONENT_PLURALS: Record<ComponentTypeName, string> = {
  fan: 'fans',
  coil: 'coils',
  filter: 'filters',
  eliminator: 'eliminators',
  evap_pack: 'evap_packs',
  gas_heater: 'gas_heaters',
  silencer: 'silencers',
  energy_wheel: 'energy_wheels',
  plate_heat_exchanger: 'plate_heat_exchangers',
  user_defined_component: 'user_defined_components',
};

/** §50.1 — partName drives classID + dispatcher names (er_wheel split, Q22). */
export const COMPONENT_PART_NAMES: Record<ComponentTypeName, string> = {
  fan: 'fan',
  coil: 'coil',
  filter: 'filter',
  eliminator: 'eliminator',
  evap_pack: 'evap_pack',
  gas_heater: 'gas_heater',
  silencer: 'silencer',
  energy_wheel: 'er_wheel', // classID component.er_wheel.model (§50.1)
  plate_heat_exchanger: 'plate_heat_exchanger',
  user_defined_component: 'user_defined_component',
};

export const classIdFor = (type: ComponentTypeName): string =>
  `component.${COMPONENT_PART_NAMES[type]}.model`;

/** §49.3 step 7 — `section:rendered` fires only for the hasRating types. */
export const SECTION_RENDERED_TYPES = new Set([
  'fan',
  'coil',
  'gas_heater',
  'plate_heat_exchanger',
  'energy_wheel',
  'er_wheel',
  'filter',
  'rotary',
]);

// ---------------------------------------------------------------------------
// Catalogue field config (§49.4 ctor wiring — extracted verbatim from the
// per-type form.js config objects).
// ---------------------------------------------------------------------------
export interface CatalogueFieldConfig {
  targetField: string;
  paramsSourceField: string;
  sourceURL: string;
  /** Legacy selectedModelURL — getFirstModelDetailed / get_by_id base
   * (defaults to sourceURL when absent, matching the legacy ctor). */
  selectedModelURL?: string;
  /** Single master-pick name (count badge class `<componentsName>_search_result`). */
  componentName: string;
  componentsName: string;
  /** Filter-tmpl JSON column keys (allowed_fields of results_filter 0). */
  paramsFields: string[];
  /** Greyed during PATCH flight (§49.2 disableFilterFields). */
  filterFields?: string[];
  /** Number of ResultsFilter instances (fan motor = 3: main/fan-link/voltage). */
  filtersCount?: number;
  /** Counts suppressed at init until lifted (filter/frame, §50.1). */
  skipFilteredCountOnInit?: boolean;
  /** noFetchCount — selection never auto-collapses count badge fetches. */
  noFetchCount?: boolean;
  /** Count badge visible in rating mode (fan/ewheel/phx master fields). */
  counterElementWhenInRatingMode?: boolean;
  /** spn column override for the smart-list rows (filter/eliminator 'info'). */
  spnCustomSourceField?: string;
  /**
   * 6e2b — accessory deselect extras (§49.4 deselectModel:836-848 + reset
   * click :616 / parity 312): owning model is an accessory and the field is
   * NOT one of {viewport, switch, contactor, overload} → reset also nulls
   * height/width/(_calc)/overall_* /distance_to_* and fires
   * `remove_accessory_from_canvas <id>`.
   */
  accessoryResetExtras?: boolean;
}

// ---------------------------------------------------------------------------
// Per-type form config (§50.1 row → literal config object, per §49.3 mapping).
// ---------------------------------------------------------------------------
export interface ComponentTypeConfig {
  type: ComponentTypeName;
  plural: string;
  partName: string;
  classID: string;
  subtabs: string[];
  withRating: boolean;
  /** gas_heater: catalogueParams parsed from the model's params JSON. */
  withoutRatingSeparateAPI?: boolean;
  /** LIVE validatedFields only (§50.1 — empty = all commented out). */
  validatedFields: string[];
  catalogueFields: CatalogueFieldConfig[];
  /** auto-single-option engine always armed (silencer — §49.3). */
  alwaysAllowAutoSelect?: boolean;
  /** Per-type CMS params doc URL override (evap_pack routes are SINGULAR:
   * cms/1/v1.0.0/ahu/evap_pack/params/ — verify 6e2a-1). */
  cmsParamsURL?: string;
  /** No per-type cms params doc at all (UDC — options come from the board
   * cms doc + master collections; legacy never fetches
   * user_defined_components/params/ — verify 6e2a-1). */
  noCmsParamsDoc?: boolean;
}

// ---------------------------------------------------------------------------
// Q15 SHIM — §50.2 frozen reach-in interface (14 members).
// ---------------------------------------------------------------------------
export interface ShimCatalogueField {
  /** #4/#5 — live filter dict + counts. */
  cmsPages: {
    results_filter: { toJSON(): Record<string, unknown> };
    cms_count: number;
    cms_full_count: number;
    /**
     * Has a count ever come back? Legacy's `cms_count` is genuinely
     * `undefined` until the first `fetchCount` resolves, and its templates
     * branch on that: `{{#if filters_count}}` blanks the badge slot in the
     * pre-count window only, while the post-fetch `update:count:*` listener
     * writes the number unconditionally, zero included
     * (filter.selector.js:204). `cms_count` above is `engine.cmsCount ?? 0`,
     * which collapses a real 0 into "not fetched yet" — read this before
     * treating a 0 as "nothing to show".
     */
    cms_count_fetched: boolean;
    /**
     * The same question for the unfiltered total. `cms_full_count` is
     * `engine.cmsFullCount ?? 0` for the same reason, so a badge that prints
     * it before the request lands reads "/ 0" — legacy prints the empty string
     * there (`${full === undefined ? '' : full}`,
     * catalogue_select_field.js:1051).
     */
    cms_full_count_fetched: boolean;
  };
  /**
   * #5c — the query string the engine counts with: EVERY results_filter
   * joined, not just index 0. `cms.components.pages.js:317-330` hands the
   * whole `results_filters` array to both the counter and the catalogue
   * collection, so legacy's smart list is constrained by the same filters as
   * the "n / N" badge above it. Exposed so a dialog that builds its own
   * catalogue request cannot drift from the count.
   */
  filtersQuery(): string;
  /**
   * #5d — the view's half of the count gate. The FORM suspends the count while
   * there is nothing to count against (the filter form: no bank configuration
   * chosen yet); the VIEW calls this with `true` when it knows a selection is
   * never coming — the bank list came back EMPTY — and the count then runs
   * anyway, so the badge shows the API's answer (0) instead of staying blank.
   */
  resolveCountGate(resolved: boolean): void;
  /** #6 — write back selection-derived filters. */
  resetFilter(
    attrs: Record<string, unknown>,
    silent?: boolean,
    filterIndex?: number
  ): void;
  /** #7 — manual-mode selected master inspection. */
  selectedModel: Record<string, unknown> | undefined;
  /**
   * #6b — the Reset control's real legacy target. `catalogue_select_field.js`
   * :608-619 binds `span.filters_reset` / `span.frames_reset` to
   * `deselectModel()`, which clears results_filter[0], nulls the master id and
   * rewrites the params field — NOT `resetFilter({})`. Exposed so the rating
   * dialogs' Reset buttons can do what the form's does.
   */
  deselectModel(): void;
  /**
   * #5b — count-change notification. The engine's counts land asynchronously
   * (refreshCounts); legacy broadcast `update:count:<id>:<url>` and the filter
   * dialog listened (filter.selector.js:196-225). A React selector mounted
   * outside the form's tree gets no re-render from `useCatalogueFields`, so it
   * subscribes here instead. Returns an unsubscribe fn.
   */
  subscribe(cb: () => void): () => void;
  /**
   * #6c — the "selection collapses onto the only candidate" engine
   * (`catalogue_select_field.js:1046` checkIfThereIsOnlyOneFoundModel). The
   * form reaches it through updateFilter, which only fires it when the count is
   * already 1; the filter dialog calls it DIRECTLY with `force` from
   * autoSelectFrame (filter.selector.js:1948), which takes the first candidate
   * whatever the count is. Exposed so the dialog can do what legacy does rather
   * than leave the Frame tab half-filled.
   */
  checkIfThereIsOnlyOneFoundModel(force?: boolean): Promise<void>;
  /**
   * #8 — embedded smart-list surgery (filter selector re-homes the singleton
   * table). CANNOT be shimmed across the React boundary — filter selector +
   * filter form migrate in the SAME cut (6e2c, §50.2 note / D6). The seam is
   * exported as an explicit surgery handle so 6e2c has one place to cut.
   */
  catalogueSelector: {
    catalogueRowsContainer: HTMLElement | null;
    mainRotationElement: HTMLElement | null;
    with_rating: boolean;
  };
}

export interface ComponentFormShim {
  /** #1 — repaint form after rating/selection commit. */
  render(dependencyData?: Record<string, unknown>): void;
  /** #2 — live form model (get/set/subscribe — Backbone-compatible surface). */
  model: FormModel;
  /** #3 — rating dialog owns the model (suppresses §49.3 render + §49.2 saves). */
  rate: boolean;
  /** #4–#8 — per-target-field catalogue reach-ins. */
  catalogueFields: Record<string, ShimCatalogueField>;
  /** #9 — fan rating curves lifecycle (set null by onManualModeSelection). */
  fan_rating_curves_model: unknown | null;
  /** #10 — commit staged mounting (accessory forms — 6e2b; stub here). */
  createPosition(
    type: string,
    planeId: number,
    mountingValue?: unknown,
    changeTagName?: boolean,
    extraData?: Record<string, unknown>
  ): Promise<unknown>;
  /** #11 — staging-model auto-size (accessory — 6e2b; stub). */
  autoSize(
    auto: boolean,
    model?: unknown,
    data?: Record<string, unknown>,
    faceVelocity?: number
  ): boolean;
  /** #12 — render staging data with the form's own getter (0-arg base must
   * tolerate the 2 staging args — §50.2 note). */
  dataGetterForRendering(
    stagingPosition?: unknown,
    stagingModel?: unknown
  ): Record<string, unknown>;
  /** #13 — face-velocity fallback (accessory; string per legacy). */
  defaultFaceVelocity: string;
  /** #14 — readiness gating before reach-ins. */
  catalogueFieldsInitializingPromise: Promise<void>;
  fullyLoading: Promise<void>;
  /**
   * 6e2c-3 — the live accessory position snapshot (`current_model.position
   * .toJSON()`). The legacy accessory selector clones `current_model.position`
   * for its staging_position_model; the React form holds the live position in
   * useAccessoryForm.positionRef but never surfaced it on the shim (the
   * dataGetterForRendering reach was a no-op because the forms don't pass one).
   * Returns {} when the accessory has no position yet (drain_pan, unplaced). */
  getPosition?: () => Record<string, unknown>;
  /**
   * 6e2c-3 fix — is `getPosition()` ANSWERABLE yet?
   *
   * The live position arrives on a SECOND round trip (`AccessoryPositionModel
   * .fetch()`), fired only once the form's detail GET has resolved — i.e.
   * strictly AFTER this shim is registered. A selector that mounts in the same
   * breath as its form (a newly created accessory auto-opens its dialog) so
   * seeds `staging_position_model` from an EMPTY `getPosition()`, and because
   * seeding is one-shot it never picks the position up: the Mounting Plane
   * select renders its `- -` placeholder and `init_position` stays `{}`, which
   * mis-diffs the position on Select as well.
   *
   * `true` ALSO when no position is coming — `withPosition: false`, or a
   * `loadPositionWhenAlreadySelected` row with no `position_id` — and when the
   * fetch failed, so a waiter can never hang. Absent on the non-accessory
   * shims; read it as `true`.
   */
  positionReady?: () => boolean;
  /**
   * 6e2c-3 fix (Item 4 — position-drop) — the WRITE sibling of getPosition.
   * The accessory selector's selectAccessory does `current_model.position.set(
   * positionChanges)` when the plane is UNCHANGED but x/y/mounting_type changed
   * (legacy accessory.selector.js:1177-1179). That `position.set` runs the
   * position model's own change→auto-PATCH pipeline (ahu/positions[/<base>]/<id>).
   * The React form holds the live position in useAccessoryForm.positionRef; this
   * surfaces a writer that arms the model (real user gesture) and `set`s the
   * diff so the same PATCH fires. No-op when there is no position yet. */
  applyPositionDiff?: (diff: Record<string, unknown>) => void;
  /**
   * 6e2c-3 fix — the section airway dims the selector needs (aw_width/aw_height/
   * aw_length). Legacy `rating_mode:activate` carries the FULL section Backbone
   * model (selection_mode.js:211 `section: self.componentView.section`), so the
   * selector reads `section.get('aw_width')` for the "Available Airway W x H"
   * readonly fields + the §51.4 mounting math dims. The React opener can only
   * pass `{id}`, so it pulls these off the form's already-derived awDimsRef. */
  getSectionDims?: () => { aw_width: number; aw_height: number; aw_length: number };
}

declare global {
  interface Window {
    /** 6d-spike agreement: per-component shim registry for legacy selectors. */
    __FORM_SHIM__?: Record<number, ComponentFormShim>;
    /** §13 transition rule — React panels mounted, legacy senders disabled. */
    __REACT_PANEL__?: boolean;
  }
}

/**
 * Registry-change subscribers. The shim is registered in an effect that only
 * runs once the form's model exists (detail GET resolved), so a selector opened
 * by the navy `#show_rating` — which MOUNTS the host form and asks it to open
 * the dialog in the same click (FormsPanel.showRating) — can mount BEFORE
 * `window.__FORM_SHIM__[id]` is there. A plain `window` read cannot see it
 * arrive; this is how a shim-driven selector waits for it.
 */
const shimListeners = new Set<() => void>();

/** Subscribe to register/unregister of ANY form shim. Returns the unsubscribe. */
export function subscribeFormShims(onChange: () => void): () => void {
  shimListeners.add(onChange);
  return () => {
    shimListeners.delete(onChange);
  };
}

/**
 * Re-notify the subscribers without a register/unregister.
 *
 * The registry is not the only thing a waiting selector blocks on: an accessory
 * form's LIVE position lands on its own round trip, after the shim is already
 * in `window.__FORM_SHIM__`. `useAccessoryForm` calls this when `positionReady()`
 * flips so a `useSyncExternalStore` gate re-reads. Extra notifications are free
 * — every gate compares its own snapshot.
 */
export function notifyFormShims(): void {
  shimListeners.forEach((cb) => cb());
}

export function registerFormShim(componentId: number, shim: ComponentFormShim): () => void {
  if (!window.__FORM_SHIM__) window.__FORM_SHIM__ = {};
  window.__FORM_SHIM__[componentId] = shim;
  shimListeners.forEach((cb) => cb());
  return () => {
    if (window.__FORM_SHIM__?.[componentId] === shim) {
      delete window.__FORM_SHIM__[componentId];
      shimListeners.forEach((cb) => cb());
    }
  };
}
